import { ReactNode, useId } from 'react';
import MuiDialog from '@mui/material/Dialog';
import Button, { Props as ButtonProps } from '@classify/react-core-components/Button';
import Typography from '@classify/react-core-components/Typography';
import Stack from '@classify/react-core-components/Stack';
import { styled } from '../styles';

const Title = styled(Typography)(({ theme: { spacing } }) => ({
  padding: spacing(3, 3, 2, 3),
}));

const ActionsWrapper = styled(Stack)(({ theme: { spacing, breakpoints } }) => ({
  padding: spacing(2, 1, 1, 1),
  justifyContent: 'flex-start',
  gap: spacing(1),
  flexDirection: 'column-reverse',
  [breakpoints.up('md')]: {
    flexDirection: 'row-reverse',
    gap: spacing(2),
  },
}));

const ButtonWrapper = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
});

const Content = styled('div')(({ theme: { spacing } }) => ({
  width: '100%',
  flexGrow: 1,
  overflow: 'auto',
  padding: spacing(0, 3),
  boxSizing: 'border-box',
}));

export interface Props {
  children: ReactNode;
  isOpen: boolean;
  onClose: (event?: {}, reason?: string) => void;
  translations?: {
    title?: string;
  };
  confirmButtonProps?: ButtonProps;
  dismissButtonProps?: Omit<ButtonProps, 'variant'>;
}

const Dialog = ({
  isOpen,
  children,
  onClose,
  translations,
  confirmButtonProps,
  dismissButtonProps,
  ...otherProps
}: Props) => {
  const titleId = `dialog-title-${useId()}`;
  const handleClose = (event: {}, reason: string) => onClose?.(event, reason);

  const hasButtons = confirmButtonProps || dismissButtonProps;

  return (
    <MuiDialog open={isOpen} onClose={handleClose} aria-labelledby={titleId} {...otherProps}>
      {translations?.title && (
        <Title component="h1" variant="h5">
          {translations?.title}
        </Title>
      )}
      <Content
        sx={{
          marginTop: translations?.title ? 0 : 3,
          marginBottom: hasButtons ? 0 : 3,
        }}
      >
        {children}
      </Content>
      {hasButtons && (
        <ActionsWrapper>
          {confirmButtonProps && (
            <ButtonWrapper>
              <Button variant="tertiary" {...confirmButtonProps} />
            </ButtonWrapper>
          )}
          {dismissButtonProps && (
            <ButtonWrapper>
              <Button variant="tertiary" {...dismissButtonProps} />
            </ButtonWrapper>
          )}
        </ActionsWrapper>
      )}
    </MuiDialog>
  );
};

export default Dialog;
